import React, { useState } from 'react';
import styled from 'styled-components';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { Megaphone, Ticket, Eye, Coins } from 'lucide-react';
import { adminApi, formatNumber, formatCurrency, formatGrowth } from '@kardume/services';
import { theme } from '@kardume/theme';
import type { MetricsRange } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  Card,
  CardHeader,
  CardBody,
  Table,
  TableContainer,
  Select,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import DataState from '../components/DataState';
import VisitIntentInsights from '../components/VisitIntentInsights';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SectionSpacer = styled.div`
  height: 1rem;
`;

const Charts = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const RANGE_OPTIONS = [
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: '12m', label: 'Últimos 12 meses' },
];

const ReportsPage: React.FC = () => {
  const [range, setRange] = useState<MetricsRange>('30d');
  const { data, loading, error, reload } = useAsync(() => adminApi.getDashboard(range), [range]);

  return (
    <>
      <PageHeader
        title="Relatórios"
        subtitle="Analise a evolução dos seus resultados no período selecionado."
        actions={
          <Select
            options={RANGE_OPTIONS}
            value={range}
            onChange={(e) => setRange(e.target.value as MetricsRange)}
          />
        }
      />

      <DataState loading={loading} error={error} onRetry={reload}>
        {data && (
          <>
            <Grid>
              <StatCard
                label="Anúncios ativos"
                value={formatNumber(data.activePromotions)}
                icon={<Megaphone size={18} />}
                iconColor={theme.primary}
              />
              <StatCard
                label="Resgates"
                value={formatNumber(data.totalRedemptions)}
                icon={<Ticket size={18} />}
                iconColor={theme.accent}
                delta={formatGrowth(data.redemptionsGrowth)}
                deltaPositive={data.redemptionsGrowth >= 0}
              />
              <StatCard
                label="Visualizações"
                value={formatNumber(data.totalViews)}
                icon={<Eye size={18} />}
                iconColor={theme.info}
                delta={formatGrowth(data.viewsGrowth)}
                deltaPositive={data.viewsGrowth >= 0}
              />
              <StatCard
                label="Cashback distribuído"
                value={formatCurrency(data.cashbackDistributed)}
                icon={<Coins size={18} />}
                iconColor={theme.warning}
              />
            </Grid>

            <Charts>
              <Card>
                <CardHeader>
                  <h3>Resgates x Visualizações</h3>
                </CardHeader>
                <CardBody>
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={mergeSeries(data.redemptionsSeries, data.viewsSeries)}>
                      <defs>
                        <linearGradient id="r1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.primary} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={theme.primary} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="r2" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={theme.accent} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={theme.accent} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: theme.textMuted }} />
                      <YAxis tick={{ fontSize: 12, fill: theme.textMuted }} />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="redemptions"
                        name="Resgates"
                        stroke={theme.primary}
                        fill="url(#r1)"
                      />
                      <Area
                        type="monotone"
                        dataKey="views"
                        name="Visualizações"
                        stroke={theme.accent}
                        fill="url(#r2)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <h3>Top anúncios</h3>
                </CardHeader>
                <CardBody>
                  <TableContainer>
                    <Table>
                      <thead>
                        <tr>
                          <th>Anúncio</th>
                          <th>Resgates</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.topPromotions.map((item) => (
                          <tr key={item.label}>
                            <td>{item.label}</td>
                            <td>{formatNumber(item.value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </Charts>
          </>
        )}
      </DataState>

      {/* Fora do `DataState` do painel de propósito: é outro endpoint e outra
          permissão, e o lojista precisa continuar vendo os resgates mesmo se
          este bloco falhar - e vice-versa. */}
      <SectionSpacer />
      <VisitIntentInsights />
    </>
  );
};

/** Merge two time series into a single dataset keyed by date. */
function mergeSeries(
  redemptions: { date: string; value: number }[],
  views: { date: string; value: number }[]
) {
  const map = new Map<string, { date: string; redemptions: number; views: number }>();
  redemptions.forEach((p) => map.set(p.date, { date: p.date, redemptions: p.value, views: 0 }));
  views.forEach((p) => {
    const existing = map.get(p.date);
    if (existing) existing.views = p.value;
    else map.set(p.date, { date: p.date, redemptions: 0, views: p.value });
  });
  return Array.from(map.values());
}

export default ReportsPage;
